import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { canCheckFileAvailability, isImageFile, isPdfFile, isOfficeFile, isVideoFile, isLocalUrl, toAbsoluteUrl } from '../../lib/filePreview'

// In-app file preview. Handles:
//   image           -> <img>
//   pdf             -> native browser <iframe>
//   word/excel/ppt  -> Microsoft Office Online embed (needs a PUBLIC https url,
//                      i.e. works on live; on localhost it falls back to download)
//   other           -> download link
export default function FilePreviewModal({ file, onClose }) {
    const { t } = useTranslation();
    const [imgError, setImgError] = useState(false);
    const [fileStatus, setFileStatus] = useState({ checking: false, missing: false });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset the load-error flag when a different file is previewed
    useEffect(() => { setImgError(false); }, [file]);
    useEffect(() => {
        let cancelled = false;
        const applyStatus = (status) => {
            Promise.resolve().then(() => {
                if (!cancelled) setFileStatus(status);
            });
        };
        const url = file?.url;
        if (!url) {
            applyStatus({ checking: false, missing: true });
            return () => { cancelled = true; };
        }
        if (!canCheckFileAvailability(url)) {
            applyStatus({ checking: false, missing: false });
            return () => { cancelled = true; };
        }
        applyStatus({ checking: true, missing: false });
        fetch(toAbsoluteUrl(url), { method: 'HEAD', cache: 'no-store' })
            .then((res) => {
                if (!cancelled) setFileStatus({ checking: false, missing: !res.ok });
            })
            .catch(() => {
                if (!cancelled) setFileStatus({ checking: false, missing: true });
            });
        return () => { cancelled = true; };
    }, [file]);
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); } };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [onClose]);
    if (!file) return null;
    const url = file.url;
    const dlNote = (msg) => (
        <div className="filepv-note">{msg} <a href={url} download={file.name}>{t('filePreview.download', { defaultValue: 'Descarcă fișierul' })}</a></div>
    );
    const missingNote = (
        <div className="filepv-note">
            {t('filePreview.missing', { defaultValue: 'Fișierul nu mai există pe server. Reîncarcă-l pe procedură sau cere fișierul persoanei care l-a încărcat.' })}
        </div>
    );

    let body;
    if (fileStatus.checking) {
        body = <div className="filepv-note">{t('filePreview.checking', { defaultValue: 'Verific fișierul…' })}</div>;
    } else if (fileStatus.missing) {
        body = missingNote;
    } else if (isImageFile(file)) {
        body = imgError
            ? dlNote(t('filePreview.imgError', { defaultValue: 'Imaginea nu a putut fi încărcată.' }))
            : <img className="filepv-img" src={url} alt={file.name} onError={() => setImgError(true)} />;
    } else if (isPdfFile(file)) {
        body = <iframe className="filepv-frame" src={url} title={file.name} />;
    } else if (isOfficeFile(file)) {
        body = isLocalUrl(url)
            ? dlNote(t('filePreview.officeLocal', { defaultValue: 'Previzualizarea Word/Excel e disponibilă doar pe linkul public (live).' }))
            : <iframe className="filepv-frame" src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(toAbsoluteUrl(url))}`} title={file.name} />;
    } else if (isVideoFile(file)) {
        body = <video className="filepv-video" src={url} controls autoPlay playsInline style={{ maxWidth: '100%', maxHeight: '78vh' }} />;
    } else {
        body = dlNote(t('filePreview.unsupported', { defaultValue: 'Acest tip de fișier nu poate fi previzualizat.' }));
    }

    return createPortal(
        <div
            className="filepv-overlay"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose?.(); }}
            role="dialog"
            aria-modal="true"
        >
            <div className="filepv-box">
                <div className="filepv-head">
                    <span className="filepv-name" title={file.name}>{file.name}</span>
                    {!fileStatus.missing && (
                        <a className="filepv-dl" href={url} download={file.name} target="_blank" rel="noopener noreferrer" title={t('filePreview.download', { defaultValue: 'Descarcă' })}><Download size={16} /></a>
                    )}
                    <button className="filepv-close" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Închide' })}><X size={18} /></button>
                </div>
                <div className="filepv-body">{body}</div>
            </div>
        </div>,
        document.body,
    );
}
